<script setup lang="ts">
import DamagesOverviewModal from '@/components/rc/shipments/DamagesOverviewModal.vue'

import { computed, reactive, ref } from 'vue'
import StatusBadge from '@/components/common/StatusBadge.vue'
import { useQuery, useQueryClient } from '@tanstack/vue-query'
import { Icon } from '@iconify/vue'
import ContainersManagementModal from '@/components/rc/shipments/ContainersManagementModal.vue'
import { ElMessage } from 'element-plus'
import {
  ShipmentType,
  type IContainer,
  type IDamageInstance,
  type IShipmentContainer,
  type IShipmentReceipt,
} from '@/types/rc/shipments'
import EditContainerModal from '@/components/rc/shipments/EditContainerModal.vue'
import { DeleteConfirmationModal } from '@/components/common/DeleteConfirmationModal'
import { DEFAULT_PAGE_SIZES, DEFAULT_PAGINATION_LAYOUT } from '@/types/shared/pagination'
import { useRouter } from 'vue-router'
import { useContainers } from '@/composables/rc/containers'
import DetailsViewLayout from '@/views/layouts/DetailsViewLayout.vue'
import ShipmentDialog from '@/components/rc/shipments/ShipmentDialog.vue'
import { getAllCarriers, getShipmentContainers, getShipmentDetails } from '@/services/rc/shipments.service'
import { useShipments } from '@/composables/rc/shipments'
import RlDetailsGrid from '@/components/common/RlDetailsGrid.vue'
import { NotifyFail } from '@/helpers/core/notifications'
import { DateTime } from 'luxon'
import { downloadLinkTarget } from '@/util'

const { deleteContainerMutation } = useContainers()

const shipmentDialogOpen = ref(false)
const shipmentBeingUpdated = ref()

const props = defineProps<{ shipmentId: number }>()
const damagesOverviewModalOpen = ref<boolean>(false)
const containerManagementModalOpen = ref<boolean>(false)
const editContainerModalOpen = ref<boolean>(false)
const selectedDamages = ref<IDamageInstance[]>([])
const selectedContainerID = ref<number>(0)
const selectedContainerNumber = ref<string>('')
const selectedContainerMiners = ref<number>(0)
const selectedContainerType = ref<string>('')

const filters = reactive({
  page: 1,
  per_page: DEFAULT_PAGE_SIZES[0],
})

const totalContainersNumber = computed(() => dataContainers.value?.total_records ?? 0)

const router = useRouter()
const queryClient = useQueryClient()

const { getShipmentDetailsQuery } = useShipments()

const shipmentId = computed(() => props.shipmentId)
const { isFetching, isError, isRefetching, data, error, refetch } = getShipmentDetailsQuery(shipmentId)

const { data: carrierOptions } = useQuery({ queryKey: ['carrier-options'], queryFn: getAllCarriers })

const {
  isFetching: isFetchingContainers,
  isError: isErrorContainers,
  isRefetching: isRefetchingContainers,
  data: dataContainers,
  error: errorContainers,
  refetch: refetchContainers,
} = useQuery({
  queryKey: ['shipment-details-containers', shipmentId, filters],
  queryFn: () => getShipmentContainers(shipmentId.value, filters),
})

const details = computed(() => {
  return [
    {
      label: 'Work Order',
      value: data.value?.work_order.number,
    },
    {
      label: 'Type',
      value: data.value?.type,
    },
    {
      label: 'Date Created',
      value: DateTime.fromISO(data.value?.created_at ?? '').toFormat('yyyy-MM-dd HH:mm:ss ZZZZ'),
    },
    {
      label: 'Status',
      value: data.value?.status,
    },
    {
      label: 'Carrier Status',
      value: data.value?.details.carrier_status ?? '-',
    },
  ]
})

const getDamagePreviewFileURL = (damages: IDamageInstance[]) => {
  if (damages.length) {
    return damages[0].file_url
  }
}

const getDamagePreviewAlt = (damages: IDamageInstance[]) => {
  if (damages.length) {
    return damages[0].description
  }
}

const openDamagePreviewModal = (cont: IContainer, e: MouseEvent) => {
  e.stopPropagation()

  selectedDamages.value = cont.damages
  selectedContainerNumber.value = cont.number
  selectedContainerID.value = cont.id

  damagesOverviewModalOpen.value = true
}

const openContainerManagementModal = () => {
  containerManagementModalOpen.value = true
}

const resetSelectedContainerData = () => {
  selectedDamages.value = []
  selectedContainerNumber.value = ''
  selectedContainerID.value = 0
}

const onContainerManagementModalClose = async (shouldUpdateContainers: boolean) => {
  containerManagementModalOpen.value = false

  if (shouldUpdateContainers) {
    await refetchContainers()
  }
}

const onContainerEditModalClose = async (shouldUpdateContainers: boolean) => {
  handleContainerEditClose()

  if (shouldUpdateContainers) {
    await refetchContainers()
  }
}

const handleContainerEditOpen = (id: number, e: MouseEvent) => {
  e.stopPropagation()
  const selectedContainer = dataContainers.value?.data.find((container) => container.id === id)

  if (!selectedContainer) {
    NotifyFail('Failed to edit selected container.')
    return
  }

  selectedContainerID.value = selectedContainer.id
  selectedDamages.value = selectedContainer.damages
  selectedContainerNumber.value = selectedContainer.number
  selectedContainerMiners.value = selectedContainer.expected_miners
  selectedContainerType.value = selectedContainer.type

  editContainerModalOpen.value = true
}

const handleContainerDeleteOpen = (containerNumber: string, e: MouseEvent) => {
  e.stopPropagation()
  DeleteConfirmationModal(
    'Delete Container',
    'Are you sure you want to delete this container? This action cannot be undone',
    containerNumber,
    deleteContainerMutation.mutateAsync,
    () => {
      queryClient.invalidateQueries({ queryKey: ['shipment-details-containers'] })
    }
  )
}

const handleContainerEditClose = () => {
  editContainerModalOpen.value = false

  selectedContainerID.value = 0
  selectedContainerMiners.value = 0
  selectedContainerType.value = ''
  selectedContainerNumber.value = ''
  selectedDamages.value = []
}

const downloadIndividualReceipt = (receipt: IShipmentReceipt) => {
  try {
    downloadLinkTarget(receipt.file_url, receipt.name)
  } catch {
    ElMessage({
      type: 'error',
      message: `Failed to download ${receipt.name}.`,
    })
  }
}

/**
 * Gets the cell class name for each cell.
 * Applies "cursor: pointer".
 *
 * @param param0 destructured ElTable column.
 */
const getCellClassName = () => {
  return 'cursor-pointer'
}

/**
 * Handles row click, navigates to container details for a given row.
 *
 * @param row ElTable row.
 * @param column ElTable column.
 */
const handleRowClick = (row: IShipmentContainer) => {
  router.push({
    name: 'rc-logistics-container-details',
    params: { containerID: row.id },
  })
}

const handleShipmentEdit = async () => {
  try {
    const shipmentDetails = await getShipmentDetails(shipmentId.value)

    const {
      id,
      work_order,
      carrier,
      tracking_number,
      bol_number,
      waybill_number,
      expected_container_types,
      expected_containers,
      expected_miners,
      type,
      delivered_date,
      picked_up_date,
      customer_drop_off,
      customer_pick_up,
      receipts,
    } = shipmentDetails

    const commonShipmentDetails = {
      id,
      work_order,
      tracking_information: {
        carrier: carrier ?? undefined,
        tracking_number: tracking_number ?? '',
        bol_number: bol_number ?? '',
        waybill_number: waybill_number ?? '',
      },
      expected_container_types: expected_container_types ?? [],
      expected_containers: expected_containers ?? '',
      expected_miners: expected_miners ?? '',
      picked_up_date: picked_up_date ?? '',
      delivered_date: delivered_date ?? '',
      type,
      receipts: receipts ?? [],
    }

    if (type === ShipmentType.Inbound) {
      shipmentBeingUpdated.value = {
        ...commonShipmentDetails,
        customer_drop_off: customer_drop_off,
      }
    } else if (type === ShipmentType.Outbound) {
      shipmentBeingUpdated.value = {
        ...commonShipmentDetails,
        customer_pick_up: customer_pick_up,
      }
    }

    shipmentDialogOpen.value = true
  } catch {
    ElMessage.error({
      message: 'Failed to refresh record prior to update, aborting.',
    })
  }
}
</script>

<template>
  <rl-error-result v-if="isError && !isRefetching" :error="error">
    <el-button @click="refetch()">
      <Icon icon="material-symbols:refresh-rounded" height="16" width="16" :inline="true" />&nbsp;Reload
    </el-button>
  </rl-error-result>
  <DetailsViewLayout
    back-button-text="Back to previous screen"
    :title="`Shipment # ${shipmentId}`"
    @on-back-click="router.back()"
  >
    <template #controls>
      <el-button type="primary" @click="handleShipmentEdit">Edit</el-button>
    </template>
    <template v-if="data">
      <RlDetailsGrid class="w-full max-w-[1920px]" :items="details" :loading="isFetching" :columns="details.length">
        <template #scope="{ label, value }">
          <StatusBadge v-if="label.toLowerCase() === 'status' && value" class="w-fit" :status="value" />
          <RouterLink
            v-else-if="label === 'Work Order'"
            :to="{
              name: 'rc-work-order-dashboard',
              params: { number: value },
            }"
          >
            <el-button link>
              <div class="inline-flex gap-2 items-center">
                {{ value }}
                <Icon icon="material-symbols:open-in-new" :height="14" />
              </div>
            </el-button>
          </RouterLink>
          <p v-else>{{ value }}</p>
        </template>
      </RlDetailsGrid>
      <el-divider />
      <div class="shipment-details__content">
        <div class="shipment-details__content__col">
          <el-card class="shipment-details__content__col-item">
            <div class="shipment-details__content__col-item__header">General</div>
            <div class="shipment-details__content__col-item__content">
              <div class="shipment-details__content__col-item__content__row">
                <div class="shipment-details__content__col-item__content__row-label">
                  {{ data.type === ShipmentType.Inbound ? 'Dropped off' : 'Picked up' }}
                </div>
                <div class="shipment-details__content__col-item__content__row-value" data-testid="shipment-carrier">
                  {{
                    data.type === ShipmentType.Inbound
                      ? data.customer_drop_off
                        ? 'Yes'
                        : 'No'
                      : data.customer_pick_up
                        ? 'Yes'
                        : 'No'
                  }}
                </div>
              </div>
              <div class="shipment-details__content__col-item__content__row">
                <div class="shipment-details__content__col-item__content__row-label">Carrier</div>
                <div class="shipment-details__content__col-item__content__row-value" data-testid="shipment-carrier">
                  {{ data.carrier }}
                </div>
              </div>
              <div class="shipment-details__content__col-item__content__row">
                <div class="shipment-details__content__col-item__content__row-label">Tracking number</div>
                <div
                  class="shipment-details__content__col-item__content__row-value"
                  data-testid="shipment-tracking-number"
                >
                  {{ data.tracking_number }}
                </div>
              </div>
              <div class="shipment-details__content__col-item__content__row">
                <div class="shipment-details__content__col-item__content__row-label">BOL number</div>
                <div class="shipment-details__content__col-item__content__row-value" data-testid="shipment-bol-number">
                  {{ data.bol_number }}
                </div>
              </div>
              <div class="shipment-details__content__col-item__content__row">
                <div class="shipment-details__content__col-item__content__row-label">Waybill number</div>
                <div
                  class="shipment-details__content__col-item__content__row-value"
                  data-testid="shipment-waybill-number"
                >
                  {{ data.waybill_number }}
                </div>
              </div>
              <div class="shipment-details__content__col-item__content__row">
                <div class="shipment-details__content__col-item__content__row-label">Shipped date</div>
                <div
                  v-if="data.picked_up_date"
                  class="shipment-details__content__col-item__content__row-value"
                  data-testid="shipment-pick-up-date"
                >
                  {{ DateTime.fromISO(data.picked_up_date).toFormat('yyyy-MM-dd HH:mm:ss ZZZZ') }}
                </div>
              </div>
              <div class="shipment-details__content__col-item__content__row">
                <div class="shipment-details__content__col-item__content__row-label">Delivered date</div>
                <div
                  v-if="data.delivered_date"
                  class="shipment-details__content__col-item__content__row-value"
                  data-testid="shipment-delivered-date"
                >
                  {{ DateTime.fromISO(data.delivered_date).toFormat('yyyy-MM-dd HH:mm:ss ZZZZ') }}
                </div>
              </div>
            </div>
          </el-card>
          <el-card class="shipment-details__content__col-item">
            <div class="shipment-details__content__col-item__header">Expected Content</div>
            <div class="shipment-details__content__col-item__content">
              <div class="shipment-details__content__col-item__content__row">
                <div class="shipment-details__content__col-item__content__row-label">Container types</div>
                <div
                  class="shipment-details__content__col-item__content__row-value"
                  data-testid="shipment-containers-type"
                >
                  {{
                    data.expected_container_types && data.expected_container_types.length
                      ? data.expected_container_types.join(', ')
                      : ''
                  }}
                </div>
              </div>
              <div class="shipment-details__content__col-item__content__row">
                <div class="shipment-details__content__col-item__content__row-label">Containers amount</div>
                <div
                  class="shipment-details__content__col-item__content__row-value"
                  data-testid="shipment-containers-amount"
                >
                  {{ data.expected_containers }}
                </div>
              </div>
              <div class="shipment-details__content__col-item__content__row">
                <div class="shipment-details__content__col-item__content__row-label">Miners amount</div>
                <div
                  class="shipment-details__content__col-item__content__row-value"
                  data-testid="shipment-miners-amount"
                >
                  {{ data.expected_miners }}
                </div>
              </div>
            </div>
          </el-card>
          <el-card v-if="data.receipts && data.receipts.length > 0" class="shipment-details__content__col-item">
            <div class="shipment-details__content__col-item__header">Receipts</div>
            <div class="shipment-details__content__col-item__content">
              <div
                v-for="receipt in data.receipts"
                :key="receipt.id"
                class="shipment-details__content__col-item__content__receipt-item"
              >
                <div class="shipment-details__content__col-item__content__receipt-item__name">
                  {{ receipt.name }}
                </div>
                <el-link type="primary" @click="downloadIndividualReceipt(receipt)">
                  <Icon icon="material-symbols:download" height="20" width="20" :inline="true" />
                  Download
                </el-link>
              </div>
            </div>
          </el-card>
        </div>
        <div class="shipment-details__content__col">
          <el-card v-if="!errorContainers" class="shipment-details__content__col-item">
            <div class="shipment-details__content__col-item__header">
              <div class="shipment-details__content__col-item__header__headline">
                <div class="shipment-details__content__col-item__header__headline__text">Containers</div>
                <span
                  v-if="totalContainersNumber"
                  class="shipment-details__content__col-item__header__headline__counter"
                  data-testid="shipment-containers-counter"
                >
                  {{ totalContainersNumber }}
                </span>
              </div>
              <div class="shipment-details__content__col-item__header__actions">
                <el-link
                  v-if="dataContainers && dataContainers.data.length"
                  type="primary"
                  data-testid="shipment-containers-add-more"
                  @click="openContainerManagementModal"
                >
                  Add More
                </el-link>
              </div>
            </div>
            <template v-if="isFetchingContainers && !dataContainers">
              <div class="shipment-details__content__col-item__content__no-containers">Loading...</div>
            </template>
            <template v-else-if="isErrorContainers && !isRefetchingContainers">
              <div class="shipment-details__content__col-item__content__no-containers">
                <el-button @click="() => refetchContainers()">Refetch Containers</el-button>
              </div>
            </template>
            <template v-else>
              <div
                v-if="!dataContainers?.data.length"
                class="shipment-details__content__col-item__content__no-containers"
              >
                <el-button @click="openContainerManagementModal">Add Containers</el-button>
              </div>
              <div v-else class="shipment-details__content__col-item__content">
                <el-table
                  v-loading="isFetchingContainers"
                  :data="dataContainers?.data"
                  width="100%"
                  style="width: 100%"
                  class="shipment-details__content__col-item__content__table"
                  :cell-class-name="getCellClassName"
                  @row-click="handleRowClick"
                >
                  <el-table-column prop="number" label="Number" min-width="96" />
                  <el-table-column prop="work_order_number" label="Work Order #" min-width="160" />
                  <el-table-column prop="type" label="Type" />
                  <el-table-column prop="expected_miners" label="Miners" />
                  <el-table-column prop="damages" label="Damages">
                    <template #default="scope">
                      <img
                        v-if="scope.row.damages.length"
                        class="shipment-details__content__col-item__content__table__damages-img"
                        :src="getDamagePreviewFileURL(scope.row.damages)"
                        :alt="getDamagePreviewAlt(scope.row.damages)"
                        data-testid="shipment-containers-damages"
                        @click="openDamagePreviewModal(scope.row as IContainer, $event)"
                      />
                    </template>
                  </el-table-column>
                  <el-table-column fixed="right" align="right" width="80">
                    <template #default="scope">
                      <div class="shipment-details__content__col-item__content__table__actions">
                        <Icon
                          class="shipment-details__content__col-item__content__table__actions-icon"
                          icon="material-symbols:edit"
                          height="20"
                          width="20"
                          :inline="true"
                          data-testid="shipment-containers-edit"
                          @click="handleContainerEditOpen(scope.row.id, $event)"
                        />
                        <Icon
                          class="shipment-details__content__col-item__content__table__actions-icon"
                          icon="material-symbols:delete-outline"
                          height="20"
                          width="20"
                          :inline="true"
                          @click="handleContainerDeleteOpen(scope.row.id, $event)"
                        />
                      </div>
                    </template>
                  </el-table-column>
                </el-table>
                <el-pagination
                  v-if="dataContainers && dataContainers.total_pages > 0"
                  v-model:current-page="filters.page"
                  v-model:page-size="filters.per_page"
                  class="shipment-details__content__col-item__content__pagination"
                  :page-sizes="DEFAULT_PAGE_SIZES"
                  :page-count="dataContainers.total_pages"
                  :total="dataContainers.total_records"
                  :disabled="isFetching"
                  :layout="DEFAULT_PAGINATION_LAYOUT"
                  background
                />
              </div>
            </template>
          </el-card>
        </div>
      </div>
    </template>
  </DetailsViewLayout>
  <DamagesOverviewModal
    v-model="damagesOverviewModalOpen"
    :damages="selectedDamages"
    :container-id="selectedContainerID"
    :container-number="selectedContainerNumber"
    @close="resetSelectedContainerData"
  />
  <ContainersManagementModal
    v-model="containerManagementModalOpen"
    :work-order="data?.work_order.number"
    :customer="data?.work_order.customer"
    :shipment="data"
    @close="onContainerManagementModalClose"
  />
  <EditContainerModal
    v-model="editContainerModalOpen"
    :container-id="selectedContainerID"
    :container-number="selectedContainerNumber"
    :container-expected-miners="selectedContainerMiners"
    :container-type="selectedContainerType"
    :damages="selectedDamages"
    :work-order="data?.work_order.number"
    :customer="data?.work_order.customer"
    @close="onContainerEditModalClose"
  />
  <ShipmentDialog
    v-if="carrierOptions"
    v-model:open="shipmentDialogOpen"
    :shipment="shipmentBeingUpdated"
    :carriers-options="carrierOptions"
    :kind="shipmentBeingUpdated?.type"
    @closed="shipmentDialogOpen = false"
  />
</template>

<style scoped lang="scss">
.el-divider {
  margin: 0;
  color: var(--fill-tertiary);
}

.shipment-details {
  &__content {
    display: flex;
    flex-direction: row;
    justify-content: space-between;

    &__col {
      width: 49%;

      &-item {
        margin-bottom: 25px;
        background: var(--fill-secondary);
        border: none;
        box-shadow: none;
        border-radius: 8px;

        &__header {
          margin-bottom: 25px;
          display: flex;
          flex-direction: row;
          justify-content: space-between;

          &__headline {
            display: flex;
            flex-direction: row;
            align-items: center;

            &__text {
              font-size: 16px;
              font-weight: 600;
              line-height: 32px;
            }

            &__counter {
              font-size: 12px;
              padding: var(--spacing-spacing-4xs) var(--spacing-spacing-xs);
              font-weight: 400;
              color: var(--el-color-white);
              margin-left: 5px;
              border-radius: var(--spacing-spacing-2xl);
              background: var(--component-fill-gray);
            }
          }
        }

        &__content {
          &__table {
            tr {
              background: transparent !important;
            }

            &__damages {
              &-img {
                width: 32px;
                height: 32px;
                border-radius: 5px;
                cursor: pointer;
              }
            }

            &__actions {
              display: flex;
              flex-direction: row;
              justify-content: space-between;

              &-icon {
                cursor: pointer;
              }
            }
          }

          &__row {
            display: flex;
            flex-direction: row;
            margin-bottom: 15px;

            &-label {
              font-size: 14px;
              font-weight: 400;
              color: var(--text-tertiary);
              margin-right: 10px;
              min-width: 150px;
            }

            &-value {
              font-size: 14px;
              font-weight: 400;
              color: var(--text-primary);
            }

            &:last-of-type {
              margin-bottom: 0;
            }
          }

          &__no-containers {
            display: flex;
            flex-direction: row;
            justify-content: space-around;
          }

          &__pagination {
            display: flex;
            justify-content: flex-end;
            margin-top: 15px;
          }

          &__receipt-item {
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 15px;
            padding: 10px;
            background: var(--fill-tertiary);
            border-radius: 6px;

            &:last-of-type {
              margin-bottom: 0;
            }

            &__name {
              font-size: 14px;
              font-weight: 500;
              color: var(--text-primary);
              flex: 1;
              margin-right: 15px;
            }
          }
        }

        &:last-child {
          margin-bottom: 0;
        }
      }
    }

    ::v-deep(.el-table tr) {
      background: transparent;
    }

    ::v-deep(.el-table th.el-table__cell) {
      background: transparent;
    }
  }
}
</style>
